import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class Voronoi extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.voronoi',
        group: 'Spatial Analysis',
        title: 'ST Voronoi',
        description: `
      This component computes a Voronoi tesellation based on a input table with points.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'lines',
          type: WorkflowComponentParamType.Boolean,
          title: 'Return lines',
          default: false,
          providers: [Provider.BigQuery, Provider.Snowflake, Provider.Redshift, Provider.Postgres]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    let fn = 'ST_VORONOIPOLYGONS'
    if (params.lines ?? true) {
      fn = 'ST_VORONOILINES'
    }
    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = `WITH
                        _extent AS (
                          SELECT ST_EXTENT(${preparedParams.geo}) ext
                          FROM
                            ${connection.quoteName(preparedParams.source as string)}
                        ),
                        _bbox AS (
                          SELECT [ext.xmin, ext.ymin, ext.xmax, ext.ymax] bbox
                          FROM _extent
                        ),
                        _pts AS (
                          SELECT
                            ARRAY_AGG(${preparedParams.geo}) pts
                          FROM
                            ${connection.quoteName(preparedParams.source as string)}
                        ),
                        _tessellation AS (
                          SELECT ${connection.quoteName(
                            envVars.analyticsToolboxDataset as string
                          )}.${fn}(pts, bbox) geom
                          FROM _pts, _bbox
                        )
                        SELECT geom
                        FROM _tessellation, UNNEST(_tessellation.geom) geom`

        query = connection.getCreateTempTableQuery(tablename, select, codeType, null)
        break
      }
      case Provider.Snowflake: {
        if (dryRun) {
          query = connection.getCreateTempTableQuery(
            tablename,
            `SELECT ${preparedParams.geo} AS geom
              FROM ${connection.quoteName(preparedParams.source as string)}`,
            codeType,
            null
          )
        } else {
          const tableSelect = `WITH allpts AS (
                                SELECT
                                  ST_COLLECT(${preparedParams.geo}) pts
                                FROM
                                  ${connection.quoteName(preparedParams.source as string)}
                              ),
                              extent AS(
                                SELECT
                                  ARRAY_CONSTRUCT(
                                    ST_XMIN(pts),
                                    ST_YMIN(pts),
                                    ST_XMAX(pts),
                                    ST_YMAX(pts)
                                  ) extent
                                FROM
                                  allpts
                              ),
                              tessellation AS (
                                SELECT ${connection.quoteName(
                                  envVars.analyticsToolboxDataset as string
                                )}.${fn}(:pts, extent) geo
                                FROM extent
                              )
                              SELECT TO_GEOGRAPHY(VALUE) AS geom
                              FROM tessellation, LATERAL FLATTEN(input => geo) geom`
          const createTableQuery = connection.getCreateTempTableQuery(tablename, tableSelect, codeType, null)
          query = `LET pts ARRAY;
                SELECT
                  ARRAY_AGG(ST_ASGEOJSON(${preparedParams.geo})::STRING) pts
                INTO
                  pts
                FROM
                  ${connection.quoteName(preparedParams.source as string)};
                ${createTableQuery}`
        }
        break
      }
      case Provider.Postgres: {
        const select = `WITH allpts AS (
                          SELECT
                            ST_COLLECT(${preparedParams.geo}) pts
                          FROM
                            ${connection.quoteName(preparedParams.source as string)}
                        ),
                        tessellation AS (
                        SELECT
                          ${fn} (
                              pts, 0.0, ST_ENVELOPE(pts)
                          ) geom
                        FROM
                          allpts
                      )
                      SELECT (ST_DUMP(geom)).geom geom
                      FROM tessellation`
        query = connection.getCreateTempTableQuery(tablename, select, codeType, null)
        break
      }
      case Provider.Redshift: {
        const select = `WITH aggregated AS (
                        SELECT
                          ST_COLLECT(${preparedParams.geo}) pts
                        FROM
                          ${connection.quoteName(preparedParams.source as string)}
                      ),
                      tessellation AS (
                        SELECT
                          ST_GEOMFROMGEOJSON(
                            ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.${fn} (pts)
                          ) geo
                        FROM
                          aggregated
                      ),
                      idx AS (
                        SELECT
                          ROW_NUMBER() OVER () i
                        FROM
                          ${connection.quoteName(preparedParams.source as string)}
                      ),
                      allpolygons AS (
                        SELECT ST_GEOMETRYN(geo, i::integer) geom
                        FROM tessellation, idx
                      )
                      SELECT *
                      FROM allpolygons
                      WHERE geom IS NOT NULL`
        query = connection.getCreateTempTableQuery(tablename, select, codeType, null)
        break
      }
      case Provider.DatabricksRest: {
        const select = `WITH allpts AS (
          SELECT /*+ REPARTITION(40) */
          SEDONA_ST_COLLECT(
            SEDONA_ST_GEOMFROMWKB(
              ${connection.processOutputGeogColumn(
                `ST_UNION_AGG(${connection.processInputGeogColumn(params.geo as string)})`
              )}
            )) pts
          FROM
            ${connection.quoteName(preparedParams.source as string)}
        ),
        tessellation AS (
          SELECT /*+ REPARTITION(40) */
            EXPLODE(SEDONA_ST_DUMP(SEDONA_ST_VORONOIPOLYGONS(pts, 0, SEDONA_ST_ENVELOPE(pts)))) geo
          FROM allpts
        )
        SELECT /*+ REPARTITION(40, geom) */
          SEDONA_ST_ASBINARY(geo) AS geom
        FROM tessellation`

        query = connection.getCreateTempTableQuery(tablename, select, codeType, null)
        break
      }
      default:
        return null
    }

    return {
      query: query,
      outputs: { result: tablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.0.0'
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ connection }: GetOutputSchemaParams): TableSchema {
    return [
      {
        name: 'geom',
        type: connection.getGeoDataType()
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { Voronoi }
